/**
 * Assinatura dentro do app (IAP) via RevenueCat.
 *
 * POR QUE REVENUECAT E NAO STOREKIT/PLAY BILLING DIRETO: validacao de recibo,
 * renovacao, reembolso, upgrade e reconciliacao entre App Store e Play sao o
 * tipo de codigo em que um bug silencioso vira cobranca errada. O RevenueCat
 * cobre as duas lojas com a mesma integracao e um unico webhook.
 *
 * Na web isto e inteiramente no-op: quem assina pelo site continua no pagar.me.
 *
 * IDENTIFICADOR: usamos o slug do tenant como app_user_id. E estavel, nao
 * contem dado pessoal, e e o mesmo que o webhook usa para achar o tenant.
 */

import { isNative, getPlatform, getPlugin } from './native';

// Chaves publicas do RevenueCat (podem ir no bundle; nao sao segredo).
const CHAVES = {
  ios: process.env.REACT_APP_REVENUECAT_IOS || '',
  android: process.env.REACT_APP_REVENUECAT_ANDROID || '',
};

let cache = null;
let configuradoPara = null;

function plugin() {
  return getPlugin('Purchases');
}

export function disponivel() {
  const chave = CHAVES[getPlatform()];
  return isNative() && !!plugin() && !!chave;
}

/**
 * Embrulha o plugin num objeto comum de proposito: alguns plugins expoem um
 * Proxy que encaminha qualquer acesso de propriedade ao nativo, e retorna-lo
 * de uma funcao async faz o JavaScript ler `.then` nele. Ver platform/biometrics.js.
 */
async function api(slugTenant) {
  const p = plugin();
  const chave = CHAVES[getPlatform()];
  if (!p || !chave) return null;

  // Reconfigura se o tenant mudou (troca de conta no mesmo aparelho).
  if (!cache || configuradoPara !== slugTenant) {
    try {
      await p.configure({ apiKey: chave, appUserID: slugTenant || null });
      cache = { p };
      configuradoPara = slugTenant;
    } catch {
      return null;
    }
  }
  return cache;
}

/**
 * Preco e descricao vindos da loja — nunca fixos no codigo.
 * A loja e quem manda no preco exibido (moeda local, promocao, imposto).
 */
export async function ofertas(slugTenant) {
  const a = await api(slugTenant);
  if (!a) return { ok: false, motivo: 'indisponivel' };
  try {
    const r = await a.p.getOfferings();
    const atual = r?.current;
    const pacotes = (atual?.availablePackages || []).map(pk => ({
      id: pk.identifier,
      produto: pk.product?.identifier,
      precoFormatado: pk.product?.priceString,
      // Preco numerico e periodo saem daqui para a tela poder comparar planos
      // (ex.: quanto o anual economiza). Formatar na mao seria errado — moeda,
      // imposto e arredondamento sao da loja.
      preco: typeof pk.product?.price === 'number' ? pk.product.price : null,
      periodo: pk.packageType || null,   // MONTHLY | ANNUAL | ...
      titulo: pk.product?.title,
      descricao: pk.product?.description,
      _pacote: pk,
    }));
    if (!pacotes.length) return { ok: false, motivo: 'sem-ofertas' };
    return { ok: true, pacotes };
  } catch {
    return { ok: false, motivo: 'erro' };
  }
}

/**
 * Compra. O acesso NAO e liberado aqui: quem libera e o webhook do RevenueCat
 * atualizando o Tenant no backend. Assim o estado de assinatura tem uma fonte
 * unica, e nao depende do app ter conseguido avisar o servidor.
 *
 * motivo: 'cancelado' | 'ja-assinante' | 'indisponivel' | 'erro'
 */
export async function comprar(slugTenant, pacote) {
  const a = await api(slugTenant);
  if (!a) return { ok: false, motivo: 'indisponivel' };
  try {
    await a.p.purchasePackage({ aPackage: pacote._pacote || pacote });
    return { ok: true };
  } catch (e) {
    const msg = ((e && (e.message || e.code)) || '').toString().toLowerCase();
    if (msg.includes('cancel')) return { ok: false, motivo: 'cancelado' };
    if (msg.includes('already') || msg.includes('owned')) return { ok: false, motivo: 'ja-assinante' };
    return { ok: false, motivo: 'erro' };
  }
}

/**
 * Restaurar compras. Exigido pelas duas lojas: quem trocou de aparelho ou
 * reinstalou precisa recuperar a assinatura sem pagar de novo.
 */
export async function restaurar(slugTenant) {
  const a = await api(slugTenant);
  if (!a) return { ok: false, motivo: 'indisponivel' };
  try {
    const r = await a.p.restorePurchases();
    const ativo = Object.keys(r?.customerInfo?.entitlements?.active || {}).length > 0;
    return { ok: true, ativo };
  } catch {
    return { ok: false, motivo: 'erro' };
  }
}
